import { DateTime } from 'luxon';

import { TIME_OPTIONS } from 'components/constants/common';

import { WARSAW_TZ } from './zoomTime';

/**
 * Lesson time of day (ТЗ 0100).
 *
 * `GroupLesson.startTime/endTime` and `DayOfClass.startTime/endTime` are the
 * Europe/Warsaw wall time of the lesson on its `date` (GraphQL `Time`,
 * `HH:mm:ss`). They are shown as-is (`HH:mm`, no seconds) and turned into an
 * instant only in the Warsaw zone — never in the browser zone.
 */

const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/;

const pad = (value: number): string => String(value).padStart(2, '0');

/** `HH:mm[:ss]` → `HH:mm`; empty string for a missing/unparseable value. */
export const formatLessonTime = (raw?: string | null): string => {
  const match = TIME_RE.exec((raw || '').trim());
  if (!match) {
    return '';
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return '';
  }
  return `${pad(hours)}:${pad(minutes)}`;
};

/** `19:00–22:00`, `19:00` (no end) or '' (no start). */
export const formatLessonRange = (
  start?: string | null,
  end?: string | null,
): string => {
  const from = formatLessonTime(start);
  if (!from) {
    return '';
  }
  const to = formatLessonTime(end);
  return to ? `${from}–${to}` : from;
};

/**
 * Select option for a stored time, matched by `HH:mm` (seconds ignored).
 * A time off the 15-minute grid (e.g. 19:10 set elsewhere) still gets an
 * option of its own so the form never shows an empty field.
 */
export const toLessonTimeOption = (
  raw?: string | null,
): { value: string; label: string } | null => {
  const label = formatLessonTime(raw);
  if (!label) {
    return null;
  }
  return (
    TIME_OPTIONS.find((option) => option.label === label) || {
      value: `${label}:00`,
      label,
    }
  );
};

/**
 * Instant a lesson starts at: the aware `startsAt` from the API when present,
 * otherwise `date` + `startTime` read as Europe/Warsaw wall time (start of the
 * Warsaw day when there is no time). `null` when the date is unusable.
 */
export const lessonStartInWarsaw = (lesson: {
  date?: string | null;
  startTime?: string | null;
  startsAt?: string | null;
}): DateTime | null => {
  if (lesson.startsAt) {
    const aware = DateTime.fromISO(lesson.startsAt, { zone: WARSAW_TZ });
    if (aware.isValid) {
      return aware;
    }
  }
  if (!lesson.date) {
    return null;
  }
  const day = DateTime.fromFormat(lesson.date, 'yyyy-LL-dd', {
    zone: WARSAW_TZ,
  });
  if (!day.isValid) {
    return null;
  }
  const match = TIME_RE.exec((lesson.startTime || '').trim());
  if (!match) {
    return day;
  }
  const withTime = day.set({
    hour: Number(match[1]),
    minute: Number(match[2]),
    second: Number(match[3] || 0),
  });
  return withTime.isValid ? withTime : day;
};

/**
 * Instant a lesson ENDS at: the aware `endsAt` from the API when present,
 * otherwise `date` + `endTime` as Warsaw wall time — the next day when the
 * end is before the start (an overnight lesson, the backend's `lesson_end`).
 * Without an end time the lesson lasts to the end of its Warsaw day (the
 * backend's `lesson_is_over`). `null` when the date is unusable.
 */
export const lessonEndInWarsaw = (lesson: {
  date?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  endsAt?: string | null;
}): DateTime | null => {
  if (lesson.endsAt) {
    const aware = DateTime.fromISO(lesson.endsAt, { zone: WARSAW_TZ });
    if (aware.isValid) {
      return aware;
    }
  }
  if (!lesson.date) {
    return null;
  }
  const day = DateTime.fromFormat(lesson.date, 'yyyy-LL-dd', {
    zone: WARSAW_TZ,
  });
  if (!day.isValid) {
    return null;
  }
  const end = TIME_RE.exec((lesson.endTime || '').trim());
  if (!end) {
    return day.plus({ days: 1 });
  }
  const start = TIME_RE.exec((lesson.startTime || '').trim());
  const endMinutes = Number(end[1]) * 60 + Number(end[2]);
  const startMinutes = start ? Number(start[1]) * 60 + Number(start[2]) : null;
  const base =
    startMinutes !== null && endMinutes < startMinutes
      ? day.plus({ days: 1 })
      : day;
  const withTime = base.set({
    hour: Number(end[1]),
    minute: Number(end[2]),
    second: Number(end[3] || 0),
  });
  return withTime.isValid ? withTime : null;
};

/* ── Длительность занятия при вводе (перепроверка F8) ──────────────────── */

/**
 * Код отказа сервера: `customErrors[].code` у createGroup / updateGroup
 * (форма дня занятий) и updateJournal, поле `end_time`.
 */
export const LESSON_TIME_ERROR_CODE = 'invalid_lesson_time';

/** Потолок длительности занятия, минуты — зеркало `MAX_LESSON_DURATION`. */
export const MAX_LESSON_MINUTES = 12 * 60;

const minutesOf = (raw?: string | null): number | null => {
  const match = TIME_RE.exec((raw || '').trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

/**
 * Пара «начало–конец» допустима — то же правило, что у сервера
 * (`lesson_time_error`): длительность > 0 и ≤ 12 ч, через полночь можно
 * (21:00–00:30). Конец раньше начала — это «через полночь», поэтому опечатка
 * «19:00–18:00» молча давала 23-часовое занятие, а «19:00–19:00» — нулевое.
 * Не задано одно из времён — не это правило (true).
 */
export const lessonTimeValid = (
  start?: string | null,
  end?: string | null,
): boolean => {
  const from = minutesOf(start);
  const to = minutesOf(end);
  if (from === null || to === null) return true;
  const duration = (to - from + 24 * 60) % (24 * 60);
  return duration > 0 && duration <= MAX_LESSON_MINUTES;
};

/** День занятий для сверки с уже сохранёнными (перепроверка G12). */
export interface ILessonDayTimes {
  weekDay?: number | null;
  startTime?: string | null;
  endTime?: string | null;
}

/** Время до минут: «18:00:00» и «18:00» — одно и то же. */
const timeKey = (raw?: string | null): string => {
  const minutes = minutesOf(raw);
  return minutes === null ? (raw || '').trim() : String(minutes);
};

const lessonDayKey = (day: ILessonDayTimes): string =>
  `${day.weekDay ?? ''}|${timeKey(day.startTime)}|${timeKey(day.endTime)}`;

/**
 * Перепроверка G12: сервер (`updateGroup`, `updateJournal`) не
 * перепроверяет длительность у дня, чей (день недели, начало, конец)
 * совпадает с уже сохранённым — старые «18:00–18:00», введённые до F8, не
 * мешают переименовать группу. Формы правки делают так же: предикат
 * «этот день — исходный, без изменений». Сохранённых нет (создание группы)
 * — исходных дней нет, проверяются все.
 */
export const savedLessonDayMatcher = (
  saved: ReadonlyArray<ILessonDayTimes> | null | undefined,
): ((day: ILessonDayTimes) => boolean) => {
  const keys = new Set((saved || []).map(lessonDayKey));
  return (day) => keys.size > 0 && keys.has(lessonDayKey(day));
};

/** Отказ сервера — про время занятия. */
export const isLessonTimeError = (
  error?: { code?: string | null; field?: string | null } | null,
): boolean => (error?.code || '') === LESSON_TIME_ERROR_CODE;

/** «19:00–18:00» из текста отказа сервера (там введённое время) или `null`. */
export const lessonTimeRangeFromMessage = (
  message?: string | null,
): string | null => {
  const match = /(\d{1,2}:\d{2})\s*[–-]\s*(\d{1,2}:\d{2})/.exec(message || '');
  return match ? `${match[1]}–${match[2]}` : null;
};

/**
 * Какие дни формы не проходят правило — их «конец» подсвечиваем. Сервер
 * отказал, а по правилу клиента все дни в порядке (правила разошлись) —
 * подсвечиваем все: лучше лишняя рамка, чем отказ без места.
 */
export const invalidLessonTimeRows = (
  days: ReadonlyArray<{ startTime?: string | null; endTime?: string | null }>,
): number[] => {
  const bad = days
    .map((day, index) =>
      lessonTimeValid(day.startTime, day.endTime) ? -1 : index,
    )
    .filter((index) => index >= 0);
  return bad.length ? bad : days.map((_, index) => index);
};

/**
 * Текст ошибки «времени занятия» из ошибок формы дней (`errors.lessonsDays`):
 * первый `endTime.message` — его показываем строкой под днями занятий.
 */
export const lessonTimeMessageOf = (rows: unknown): string | null => {
  if (!Array.isArray(rows)) return null;
  const hit = rows
    .map((row) => row as { endTime?: { message?: string } } | undefined)
    .find((row) => Boolean(row?.endTime?.message));
  return hit?.endTime?.message || null;
};
